// ============================================================
// SettlementRegister.tsx — B4: Billing → Settlement statements. The firm's
// register of every issued settlement / completion statement (SRA Accounts
// Rules record-keeping): number / version, case, date, status, net,
// deductions, the client's (or the court's) approval and the B4b hand-off
// ("Ready for client account"), with a CSV export of exactly what's listed.
// Needs "See colleagues' billing" (or admin) — the server enforces it.
// (Batch 2)
//   • paged on the server (audit M12), newest first: "Showing latest N" while
//     more exist, and "Load more" fetches the next page (cursor);
//   • the statement number sits on ONE line, left-aligned like bill numbers;
//   • the net is the NET SUM — the column says where it goes (payee-aware:
//     "To the Court Funds Office"); the CSV's "Net sum (£)" column likewise;
//   • the CSV starts with a UTF-8 BOM (Excel shows — and ’ properly), says
//     "Showing latest N statements" first when the list is capped, and is
//     named with the LONDON date (not UTC — a late-evening export in BST).
// ============================================================
import { ErrorNote } from '../shared/ActionBox'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getStatementRegisterApi } from '../../api/casevoApi'
import { STATUS_LABEL, NET_SUM_LABEL, type RegisterRow, type StatementStatus } from '../../types/settlementStatement.types'
import { B_SECONDARY_SM, BIcon, INPUT, NOTE, errorText, money } from './billingUi'
import { londonToday, ukDate } from '../../utils/civilDate'
import { navigateTo } from '../../navigation/navigate'

type Filter = 'ALL' | StatementStatus | 'READY'
const TH = 'text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft px-3 py-[9px] border-b border-casevo-border-soft whitespace-nowrap'
const TD = 'px-3 py-[10px] border-b border-casevo-border-soft align-top'

// ── CSV (RFC 4180; a formula-looking cell is neutralised) ────
const cell = (v: string | number | null | undefined): string => {
  let s = v == null ? '' : String(v)
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
const pounds = (p: number): string => (p / 100).toFixed(2)
// "Net sum (£)" — the next column ("Where the net goes") says whose it is.
export const REGISTER_COLUMNS = [
  'Statement number', 'Version', 'Status', 'Kind', 'Case', 'Our reference', 'Statement date', 'Settlement date', 'Damages received',
  'Net sum (£)', 'Total deductions (£)', 'Approval', 'Held (disputed) (£)', 'Where the net goes', 'Client account', 'Superseded by', 'Delivered', 'Content hash',
] as const
export const buildRegisterCsv = (rows: RegisterRow[]): string => [
  REGISTER_COLUMNS.map(cell).join(','),
  ...rows.map((r) => [
    r.number ?? 'Draft', r.version, STATUS_LABEL[r.status], r.kind === 'INTERIM' ? 'Interim' : 'Final', r.matter, r.matterRef,
    r.statementDate ?? '', r.settlementDate ?? '', r.damagesReceivedAt ?? '', pounds(r.netToClientPence), pounds(r.totalDeductionsPence),
    r.approvalStatus, pounds(r.heldPence), NET_SUM_LABEL[r.netDestination], r.readiness.ready ? r.readiness.label : `Not ready — ${r.readiness.reason ?? ''}`,
    r.supersededByVersion ? `v${r.supersededByVersion}` : '', r.delivered ? `${r.delivered.method} ${String(r.delivered.at).slice(0, 10)}` : '', r.contentHash ?? '',
  ].map(cell).join(',')),
].join('\r\n')
export const CSV_BOM = '﻿'
// The file as downloaded: the BOM, then — when the server capped the list —
// "Showing latest N statements" as the first line, then the table.
// (Audit fix batch 2, Low 18) `loaded`: how many the screen holds (the latest
// N) — a filtered export says "4 of the latest 50 statements (filtered)", never
// "Showing latest 4" (which read as if only 4 existed).
export const registerCsvHeading = (shown: number, loaded: number, capped: boolean): string | null =>
  !capped ? (shown < loaded ? `${shown} of ${loaded} statements (filtered)` : null)
    : shown < loaded ? `${shown} of the latest ${loaded} statements (filtered)` : `Showing latest ${loaded} statements`
export const buildRegisterCsvFile = (rows: RegisterRow[], capped: boolean, loaded: number = rows.length): string => {
  const h = registerCsvHeading(rows.length, loaded, capped)
  return `${CSV_BOM}${h ? `${cell(h)}\r\n` : ''}${buildRegisterCsv(rows)}`
}
// Named with the firm's (London) date, not the UTC one.
export const registerCsvFilename = (today: string = londonToday()): string => `Settlement statements register ${today}.csv`
const download = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

const SettlementRegister: React.FC = () => {
  const [rows, setRows] = useState<RegisterRow[] | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [more, setMore] = useState<'idle' | 'busy'>('idle')
  const [moreErr, setMoreErr] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('ALL')
  const [q, setQ] = useState('')
  useEffect(() => {
    let live = true
    getStatementRegisterApi().then((r) => { if (live) { setRows(r.rows); setNextCursor(r.nextCursor ?? null) } }).catch((e) => { if (live) setErr(errorText(e, 'Couldn’t load the settlement register.')) })
    return () => { live = false }
  }, [])
  const loadMore = useCallback(async (): Promise<void> => {
    if (!nextCursor) return
    setMore('busy'); setMoreErr(null)
    try {
      const r = await getStatementRegisterApi({ cursor: nextCursor })
      setRows((cur) => {
        const seen = new Set((cur ?? []).map((x) => x.id))
        return [...(cur ?? []), ...r.rows.filter((x) => !seen.has(x.id))]
      })
      setNextCursor(r.nextCursor ?? null)
    } catch (e) { setMoreErr(errorText(e, 'Couldn’t load more statements.')) } finally { setMore('idle') }
  }, [nextCursor])
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase()
    return (rows ?? []).filter((r) =>
      (filter === 'ALL' || (filter === 'READY' ? r.readiness.ready : r.status === filter))
      && (!term || [r.number, r.matter, r.matterRef].some((x) => (x ?? '').toLowerCase().includes(term))))
  }, [rows, filter, q])
  const counts = useMemo(() => {
    const all = rows ?? []
    return { ALL: all.length, ISSUED: all.filter((r) => r.status === 'ISSUED').length, APPROVED: all.filter((r) => r.status === 'APPROVED').length, SUPERSEDED: all.filter((r) => r.status === 'SUPERSEDED').length, READY: all.filter((r) => r.readiness.ready).length } as Record<string, number>
  }, [rows])
  if (err) return <ErrorNote className="m-4">{err}</ErrorNote>
  if (!rows) return <p className={`${NOTE} m-4`} role="status">Loading the settlement register…</p>
  const capped = !!nextCursor
  const FILTERS: [Filter, string][] = [['ALL', 'All'], ['ISSUED', 'Issued'], ['APPROVED', 'Approved'], ['READY', 'Ready for client account'], ['SUPERSEDED', 'Superseded']]
  return (
    <div aria-label="Settlement statements register">
      <div className="flex items-center gap-[10px] px-4 py-[14px] flex-wrap">
        <div role="radiogroup" aria-label="Statement status" className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface flex-wrap">
          {FILTERS.map(([f, l], i) => (
            <button key={f} type="button" role="radio" aria-checked={filter === f} onClick={() => setFilter(f)}
              className={`px-[13px] py-[7px] text-[13px] inline-flex items-center gap-[6px] whitespace-nowrap focus:outline-none casevo-focus ${i ? 'border-l border-casevo-border' : ''} ${filter === f ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'}`}>
              {l} <small className="text-[11px] opacity-70 tabular-nums">{counts[f] ?? 0}</small>
            </button>
          ))}
        </div>
        <label className="ml-auto relative"><span className="sr-only">Search statements</span>
          <BIcon name="search" size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-casevo-text-soft" />
          <input className={`${INPUT} !h-[34px] !w-[230px] pl-8 text-[13px]`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search statements, cases" /></label>
        <button type="button" className={B_SECONDARY_SM} disabled={!shown.length}
          onClick={() => download(buildRegisterCsvFile(shown, capped, rows.length), registerCsvFilename())}><BIcon name="dl" size={12} />Export CSV</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13.5px] min-w-[980px]">
          <thead><tr>
            <th className={`${TH} pl-[18px]`}>Statement</th><th className={TH}>Case</th><th className={TH}>Date</th><th className={TH}>Status</th>
            <th className={`${TH} text-right`}>Net sum</th><th className={`${TH} text-right`}>Deductions</th><th className={TH}>Approval</th><th className={TH}>Client account</th>
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="hover:bg-casevo-input">
                <td className={`${TD} pl-[18px]`}>
                  <button type="button" className="font-mono font-semibold text-casevo-forest text-left whitespace-nowrap hover:underline focus:outline-none casevo-focus rounded-[3px]"
                    onClick={() => { void navigateTo({ view: 'caseDetail', caseId: r.caseId, caseSection: 'settlement' }) }} aria-label={`Open the settlement for ${r.matter}`}>
                    {r.number ?? 'Draft'} v{r.version}
                  </button>
                  <div className="text-[11.5px] text-casevo-text-soft">{r.kind === 'INTERIM' ? 'Interim' : 'Final'}{r.capacity !== 'ADULT' ? ` · ${r.capacity === 'CHILD' ? 'child' : 'protected party'}` : ''}</div>
                </td>
                <td className={TD}><div className="font-semibold text-casevo-forest">{r.matter}</div><div className="text-[11.5px] font-mono text-casevo-text-soft">{r.matterRef}</div></td>
                <td className={`${TD} tabular-nums text-[12.5px] text-casevo-text-soft`}>{r.statementDate ? ukDate(r.statementDate) : '—'}</td>
                <td className={TD}>{STATUS_LABEL[r.status]}{r.supersededByVersion ? <div className="text-[11.5px] text-casevo-warn">Superseded by v{r.supersededByVersion}</div> : null}</td>
                <td className={`${TD} text-right`}>
                  <div className="tabular-nums font-semibold">{money(r.netToClientPence)}</div>
                  <div className="text-[11.5px] text-casevo-text-soft whitespace-nowrap">{NET_SUM_LABEL[r.netDestination]}</div>
                </td>
                <td className={`${TD} text-right tabular-nums`}>{money(r.totalDeductionsPence)}</td>
                <td className={`${TD} text-[12.5px]`}>{r.approvalStatus}</td>
                <td className={`${TD} text-[12.5px]`}>{r.readiness.ready ? <span className="text-casevo-brand dark:text-[#8fd6b0] font-semibold">{r.readiness.label}</span> : <span className="text-casevo-text-soft">{r.readiness.reason}</span>}</td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={8} className={`${TD} text-center text-casevo-text-soft`}>No settlement statements{filter !== 'ALL' || q ? ' match' : ' yet'}.</td></tr>}
          </tbody>
        </table>
      </div>
      {capped && (
        <div className="flex items-center gap-[10px] flex-wrap px-[18px] py-[10px] bg-casevo-input border-t border-casevo-border-soft rounded-b-[14px]">
          <span className={NOTE}>Showing latest {rows.length} statements. Filters, search and the CSV cover these only.</span>
          <button type="button" className={`${B_SECONDARY_SM} ml-auto`} disabled={more === 'busy'} onClick={() => { void loadMore() }}>{more === 'busy' ? 'Loading…' : 'Load more'}</button>
          {moreErr && <span role="alert" className="text-[12.5px] text-casevo-danger w-full">{moreErr}</span>}
        </div>
      )}
    </div>
  )
}
export default SettlementRegister
